import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  LocalOffer as LocalOfferIcon,
  ShoppingCart as ShoppingCartIcon,
  EventNote as EventNoteIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { TreatmentRecord } from '@/types/treatment';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';

interface TimelineCardProps {
  treatment: TreatmentRecord;
  onClick?: () => void;
}

const TimelineCard: React.FC<TimelineCardProps> = ({ treatment, onClick }) => {
  // treatment.dateが既にDateオブジェクトまたは文字列の場合を処理
  const treatmentDate = treatment.date instanceof Date 
    ? treatment.date 
    : typeof treatment.date === 'string' 
      ? parseISO(treatment.date)
      : new Date(treatment.date);
  
  const dayOfWeek = format(treatmentDate, 'E', { locale: ja });
  const formattedDate = format(treatmentDate, 'MM/dd', { locale: ja });
  const formattedTime = format(treatmentDate, 'HH:mm', { locale: ja });
  
  // Extract first 100 characters of memo/notes if available
  const memoPreview = treatment.notes?.conversation
    ? treatment.notes.conversation.slice(0, 100) + 
      (treatment.notes.conversation.length > 100 ? '...' : '')
    : '';
  
  const hasProducts = treatment.products && treatment.products.length > 0;
  
  return (
    <Card
      component={motion.div}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      sx={{
        mb: 2,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
          bgcolor: 'action.hover',
        }
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          {/* Left side - Date */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {dayOfWeek}
              </Typography>
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{ lineHeight: 1 }}
              >
                {formattedDate}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
              >
                {formattedTime}
              </Typography>
            </Box>
            
            {/* Main content */}
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" component="h3">
                  {treatment.treatmentMenu}
                </Typography>
                {hasProducts && (
                  <Tooltip title="商品購入あり">
                    <ShoppingCartIcon fontSize="small" color="success" />
                  </Tooltip>
                )}
              </Stack>
              
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Chip
                  icon={<PersonIcon />}
                  label={treatment.staffName}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<AccessTimeIcon />}
                  label={`${treatment.treatmentDuration}分`}
                  size="small"
                  variant="outlined"
                />
                {treatment.symptoms && treatment.symptoms.length > 0 && (
                  <Chip
                    icon={<LocalOfferIcon />}
                    label={treatment.symptoms[0]}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Stack>
              
              {/* Memo preview */}
              {memoPreview && (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    borderLeft: 3,
                    borderColor: 'primary.main',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="start">
                    <EventNoteIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {memoPreview}
                    </Typography>
                  </Stack>
                </Box>
              )}
              
              {/* Products */}
              {hasProducts && (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    購入商品:
                  </Typography>
                  {treatment.products!.map((product, index) => {
                    const productName = typeof product === 'string' ? product : product.name;
                    return (
                      <Chip
                        key={index}
                        label={productName}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Box>
          
          {/* Right side - View details arrow */}
          {onClick && (
            <IconButton size="small" sx={{ ml: 1 }}>
              <ArrowForwardIcon />
            </IconButton>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TimelineCard;